import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VitalSignsChart from '@/components/VitalSignsChart';
import { Card } from '@/components/ui/card';
import type { ReplitVitalSign } from '@/types/replit-dashboard';
import { isBreathingSupportVital, isWithinHoursOf } from './records-utils';

interface VitalsSectionProps {
  vitalSigns: ReplitVitalSign[];
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

type RangeId = '6h' | '24h' | '48h' | 'all';

const RANGES: Array<{ id: RangeId; hours: number | null; key: string }> = [
  { id: '6h', hours: 6, key: 'records.range6h' },
  { id: '24h', hours: 24, key: 'records.range24h' },
  { id: '48h', hours: 48, key: 'records.range48h' },
  { id: 'all', hours: null, key: 'records.rangeAll' },
];

const timeOf = (vital: ReplitVitalSign) => {
  const t = new Date(vital.recordedAt).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Vital signs with a time-range control (relative to the newest reading in
 * the record) and breathing-support readings, when present, listed first.
 */
export default function VitalsSection({ vitalSigns, onAskQuestion, variant }: VitalsSectionProps) {
  const { t } = useTranslation();
  const [range, setRange] = useState<RangeId>('24h');

  if (!vitalSigns || vitalSigns.length === 0) {
    return (
      <Card className="p-4 text-center text-gray-500 text-xs">
        {t('records.noVitalSignsDataAvailable')}
      </Card>
    );
  }

  const newest = vitalSigns.reduce((best, vital) => (timeOf(vital) > timeOf(best) ? vital : best), vitalSigns[0]).recordedAt;
  const hours = RANGES.find((r) => r.id === range)?.hours ?? null;
  const inRange = hours === null ? vitalSigns : vitalSigns.filter((vital) => isWithinHoursOf(vital.recordedAt, newest, hours));

  const hasBreathingSupport = vitalSigns.some((vital) => isBreathingSupportVital(vital.type));
  const groups: Array<{ id: string; label: string | null; vitals: ReplitVitalSign[] }> = hasBreathingSupport
    ? [
        { id: 'breathingSupport', label: t('records.breathingSupport'), vitals: inRange.filter((v) => isBreathingSupportVital(v.type)) },
        { id: 'other', label: t('records.otherVitalSigns'), vitals: inRange.filter((v) => !isBreathingSupportVital(v.type)) },
      ]
    : [{ id: 'other', label: null, vitals: inRange }];

  const buttonSize = variant === 'rail' ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden divide-x divide-gray-300" role="group" aria-label={t('records.rangeLabel')}>
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              aria-pressed={range === r.id}
              className={`${buttonSize} text-sm ${range === r.id ? 'bg-primary text-white font-medium' : 'bg-white text-text-secondary hover:bg-gray-50'}`}
              data-testid={`button-vitals-range-${r.id}`}
            >
              {t(r.key)}
            </button>
          ))}
        </div>
      </div>
      {groups.map((group) => (
        <div key={group.id} className="mb-4" data-testid={`vitals-group-${group.id}`}>
          {group.label && <h3 className="text-sm font-semibold text-text-primary mb-2">{group.label}</h3>}
          {group.vitals.length > 0 ? (
            <VitalSignsChart vitalSigns={group.vitals} onAskQuestion={onAskQuestion} showExplainAll={false} />
          ) : (
            <p className="text-sm text-text-secondary">{t('records.noVitalsInRange')}</p>
          )}
        </div>
      ))}
    </div>
  );
}
